<template>
  <div class="md-layout">
    <md-card>
      <!-- Toolbar -->
      <md-card-content>
        <div class="d-flex justify-content-between mb-2">
          <div>
            <slot name="toolbar-left"></slot>
          </div>
          <div class="d-flex">
            <md-field>
              <md-input
                  v-model="query"
                  type="search"
                  placeholder="Search..."
                  clearable
                  @input="handleSearch"
              />
            </md-field>

            <md-field class="ml-2">
              <md-select v-model="pagination.perPage" name="perPage" @md-selected="handlePerPageChange">
                <md-option v-for="opt in perPageOptions" :key="opt" :value="opt">
                  {{ opt }}
                </md-option>
              </md-select>
            </md-field>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center p-4">
          <md-progress-spinner md-mode="indeterminate" :md-diameter="40" />
        </div>

        <!-- Table -->
        <md-table v-else :value="table" @md-sort="onSort">
          <!-- Header Row -->
          <md-table-row class="md-table-row-header">
            <!-- Dynamic Column Headers -->
            <md-table-head
                v-for="col in visibleColumns"
                :key="col.field"
                :md-sort-by="col.sortable !== false ? col.field : undefined"
                :md-sort-direction="sortation.field === col.field ? sortation.order : null"
            >
              {{ col.label }}
            </md-table-head>

            <!-- Actions Header -->
            <md-table-head v-if="hasActionsSlot">
              Actions
            </md-table-head>
          </md-table-row>

          <!-- Data Rows -->
          <md-table-row v-for="(item, index) in table" :key="index">
            <md-table-cell
                v-for="col in visibleColumns"
                :key="col.field"
            >
              <slot :name="'cell-' + col.field" :item="item">
                {{ formatCell(item, col) }}
              </slot>
            </md-table-cell>

            <!-- Actions Cell -->
            <md-table-cell v-if="hasActionsSlot">
              <slot name="actions" :item="item"></slot>
            </md-table-cell>
          </md-table-row>

          <!-- Empty State -->
          <md-table-row v-if="table.length === 0">
            <md-table-cell :md-colspan="columnCount" class="text-center">
              <slot name="empty">
                <div class="p-4 text-muted">No data available</div>
              </slot>
            </md-table-cell>
          </md-table-row>
        </md-table>
      </md-card-content>

      <!-- Pagination -->
      <md-card-actions v-if="total > 0" md-alignment="space-between">
        <div class="pagination-info">
          Showing {{ from + 1 }} to {{ to }} of {{ total }} entries
        </div>
        <pagination
            v-model="pagination.currentPage"
            :per-page="pagination.perPage"
            :total="total"
            @input="handlePageChange"
        />
      </md-card-actions>
    </md-card>
  </div>
</template>

<script>
import _ from 'lodash';
import Pagination from '@/components/Pagination';

export default {
  name: 'DataTable',

  components: {
    Pagination
  },

  props: {
    columns: {
      type: Array,
      required: true,
      validator: (columns) => columns.every(col => col.field && typeof col.field === 'string')
    },
    fetchData: {
      type: Function,
      required: true
    },
    perPageOptions: {
      type: Array,
      default: () => [5, 10, 25, 50]
    },
    initialSort: {
      type: Object,
      default: null
    },
    initialPerPage: {
      type: Number,
      default: 5
    },
    initialQuery: {
      type: String,
      default: ''
    },
    debounceDelay: {
      type: Number,
      default: 300
    },
    autoLoad: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      table: [],
      query: this.initialQuery,
      sortation: this.initialSort || {
        field: this.columns[0]?this.columns[0].field : 'id',
        order: 'asc'
      },
      pagination: {
        perPage: this.initialPerPage,
        currentPage: 1
      },
      total: 0,
      loading: false,
      debouncedGetList: null
    };
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      return Math.min(this.from + this.pagination.perPage, this.total);
    },

    hasActionsSlot() {
      return !!this.$slots.actions || !!this.$scopedSlots.actions;
    },

    visibleColumns() {
      return this.columns.filter(col => col.visible !== false);
    },

    columnCount() {
      let count = this.visibleColumns.length;
      if (this.hasActionsSlot) count++;
      return count;
    }
  },

  watch: {
    sortation: {
      handler() {
        this.resetAndFetch();
      },
      deep: true
    },

    'pagination.perPage'() {
      this.resetAndFetch();
    },

    query() {
      this.resetPageAndDebounce();
    }
  },

  created() {
    this.debouncedGetList = _.debounce(this.getList, this.debounceDelay);

    if (this.autoLoad) {
      this.getList();
    }
  },

  beforeDestroy() {
    if (this.debouncedGetList) {
      this.debouncedGetList.cancel();
    }
  },

  methods: {
    /**
     * Handle sort event from md-table-head
     */
    handleSort(field, event) {
      if (this.sortation.field === field) {
        // Toggle order
        this.sortation.order = this.sortation.order === 'asc' ? 'desc' : 'asc';
      } else {
        // New field, default to asc
        this.sortation.field = field;
        this.sortation.order = 'asc';
      }
      // The watch will trigger the fetch
    },

    /**
     * Reset to page 1 and fetch data
     */
    resetAndFetch() {
      this.pagination.currentPage = 1;
      this.getList();
    },

    /**
     * Reset page and trigger debounced search
     */
    resetPageAndDebounce() {
      this.pagination.currentPage = 1;
      this.debouncedGetList();
    },

    /**
     * Handle search input
     */
    handleSearch() {
      this.resetPageAndDebounce();
    },

    /**
     * Handle per page change
     */
    handlePerPageChange() {
      this.resetAndFetch();
    },

    /**
     * Handle page change
     */
    handlePageChange() {
      this.getList();
    },

    /**
     * Fetch data from API
     */
    async getList() {
      if (this.loading) return;

      this.loading = true;

      try {
        // Build sort parameter
        let sortParam = null;
        if (this.sortation.field) {
          sortParam = this.sortation.order === 'desc'
              ? `-${this.sortation.field}`
              : this.sortation.field;
        }

        const params = {
          sort: sortParam,
          filter: this.query ? { search: this.query } : {},
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage
          }
        };

        const result = await this.fetchData(params);

        // Handle different response formats
        if (Array.isArray(result)) {
          this.table = result;
          this.total = result.length;
        } else if (result.data && typeof result.total === 'number') {
          this.table = result.data;
          this.total = result.total;
        } else {
          this.table = result.data || [];
          this.total = result.total || this.table.length;
        }

        // Emit success event
        this.$emit('load-success', { data: this.table, total: this.total, params });

      } catch (error) {
        console.error('Error fetching table data', error);
        this.$emit('load-error', error);
        this.table = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    loadData() {
      return this.getList();
    },

    /**
     * Format cell value based on column type
     */
    formatCell(item, col) {
      const value = _.get(item, col.field);
      // const value = item[col.field];
      console.log(col.label);

      if (value === undefined || value === null) {
        return col.defaultValue || '';
      }

      switch (col.type) {
        case 'boolean':
          return value ? (col.trueLabel || 'Yes') : (col.falseLabel || 'No');

        case 'date': {
          if (!value) return '';
          const date = new Date(value);
          return isNaN(date.getTime()) ? value : date.toLocaleString();
        }
        case 'number':
          if (col.format) {
            return col.format(value);
          }
          return value.toLocaleString();

        case 'currency':
          return new Intl.NumberFormat(col.locale || 'en-US', {
            style: 'currency',
            currency: col.currency || 'TZS'
          }).format(value);

        case 'percentage':
          return `${(value * 100).toFixed(col.decimals || 2)}%`;

        default:
          return value;
      }
    },

    /**
     * Manually refresh the table
     */
    refresh() {
      this.getList();
    },

    /**
     * Reset all filters and sorting
     */
    reset() {
      this.query = '';
      this.sortation = this.initialSort || {
        field: this.columns[0]?this.columns[0].field : 'id',
        order: 'asc'
      };
      this.pagination.currentPage = 1;
      this.pagination.perPage = this.initialPerPage;
      this.getList();
    },

    /**
     * Export current data (if parent provides export function)
     */
    async exportData(format = 'csv') {
      this.$emit('export', { format, data: this.table });
    },
    /**
     * Handle sort event from md-table
     */
    onSort({ sortBy, sortDirection }) {
      if (sortBy) {
        this.sortation.field = sortBy;
        this.sortation.order = sortDirection;
        this.resetAndFetch();
      }
    },
  }
};
</script>

<style scoped>
.md-layout {
  width: 100%;
}

.d-flex {
  display: flex;
  align-items: center;
}

.justify-content-between {
  justify-content: space-between;
}

.ml-2 {
  margin-left: 0.5rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.p-4 {
  padding: 1.5rem;
}

.text-center {
  text-align: center;
}

.text-muted {
  color: rgba(0, 0, 0, 0.54);
}

.pagination-info {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.54);
}

/* Sortable header styling */
.sortable-header {
  cursor: pointer;
  user-select: none;
}

.sortable-header:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .d-flex {
    flex-direction: column;
    align-items: stretch;
  }

  .ml-2 {
    margin-left: 0;
    margin-top: 0.5rem;
  }

  .justify-content-between {
    flex-direction: column;
  }
}

/* Loading overlay */
.md-progress-spinner {
  margin: 0 auto;
}
</style>